import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { formatCLP } from '../lib/format';
import type { ReportSummary } from '../types';

function isoDaysAgo(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function ReportsPanel() {
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState<ReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setReport(await api.admin.report(from, to));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const totalRevenueByDay = report?.byDay.reduce((max, row) => Math.max(max, row.revenue), 0) || 1;

  return (
    <div className="dashboard-stack">
      <section className="admin-card">
        <div className="admin-card-head">
          <h2>Reportes de venta</h2>
          <div className="header-actions-row">
            <label className="inline-field">
              Desde
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="inline-field">
              Hasta
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button type="button" className="primary-button compact" onClick={load} disabled={loading}>
              {loading ? 'Cargando...' : 'Aplicar'}
            </button>
            <button type="button" className="ghost-button compact" onClick={() => api.admin.exportOrders()}>
              Export pedidos CSV
            </button>
          </div>
        </div>

        {error && <p className="form-message tone-error">{error}</p>}

        <div className="admin-grid" style={{ marginTop: 16 }}>
          <article className="metric-card">
            <span>Ingresos</span>
            <strong>{formatCLP(report?.totalRevenue || 0)}</strong>
          </article>
          <article className="metric-card">
            <span>Pedidos válidos</span>
            <strong>{report?.totalOrders || 0}</strong>
          </article>
          <article className="metric-card">
            <span>Ticket promedio</span>
            <strong>{formatCLP(report?.averageTicket || 0)}</strong>
          </article>
          <article className="metric-card">
            <span>Días con ventas</span>
            <strong>{report?.byDay.filter((d) => d.revenue > 0).length || 0}</strong>
          </article>
        </div>
      </section>

      <section className="admin-card">
        <h2>Ventas por día</h2>
        {report && report.byDay.length > 0 ? (
          <div className="report-bars">
            {report.byDay.map((row) => (
              <div className="report-bar-row" key={row.date}>
                <span className="report-bar-date">{new Date(row.date + 'T12:00:00Z').toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}</span>
                <div className="report-bar-track">
                  <div className="report-bar-fill" style={{ width: `${(row.revenue / totalRevenueByDay) * 100}%` }} />
                </div>
                <span className="report-bar-value">{formatCLP(row.revenue)}</span>
                <span className="report-bar-orders">{row.orders} ped.</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted">Sin datos para el rango seleccionado.</p>
        )}
      </section>

      <div className="admin-grid two-col">
        <section className="admin-card">
          <h2>Top productos</h2>
          {report && report.byProduct.length > 0 ? (
            <ul className="top-products">
              {report.byProduct.map((p, i) => (
                <li key={p.name + i}>
                  <span className="top-rank">{String(i + 1).padStart(2, '0')}</span>
                  <div className="top-meta">
                    <strong>{p.name}</strong>
                    <small>{p.quantity} unidades</small>
                  </div>
                  <span className="top-revenue">{formatCLP(p.revenue)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="muted">Sin datos.</p>}
        </section>

        <section className="admin-card">
          <h2>Por categoría</h2>
          {report && report.byCategory.length > 0 ? (
            <ul className="top-products">
              {report.byCategory.map((c, i) => (
                <li key={c.category}>
                  <span className="top-rank">{String(i + 1).padStart(2, '0')}</span>
                  <div className="top-meta">
                    <strong>{c.category}</strong>
                    <small>{c.quantity} unidades</small>
                  </div>
                  <span className="top-revenue">{formatCLP(c.revenue)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="muted">Sin datos.</p>}
        </section>
      </div>
    </div>
  );
}
